import store from "../store";

import { dispatchify } from "aurelia-store";
import { applicationLogger } from "../../logging/logger";
import { updateLocation } from "./location-actions";

import { Container } from "aurelia-framework";
import { IState } from "../state";
import { Api } from "../../services/api";

import * as Linq from "linq-es2015";
import { MeasurementViewModel } from "../../models/measurement-view-model";
import { Device } from "../../models/device";
import { Location } from "../../models/location";

import { MeasurementRequestViewModel } from "../../models/measurement-request-view-model";
import { StatusColors } from "../../pages/common/status-colors";
import { Structure } from "../../models/structure";
import { Zone } from "../../models/zone";

const api: Api = Container.instance.get(Api);

function getDevice(location: Location, measurementData: MeasurementViewModel): Device {

	if (location === undefined) 
	{
		return null;
	}

	const zone = Linq.AsEnumerable<Zone>(location.zones).FirstOrDefault(zn => zn.id === measurementData.metadata.zoneId);
	if (zone == null) {
		return null;
	}

	const structure = Linq.AsEnumerable<Structure>(zone.structures).FirstOrDefault(struct => struct.id === measurementData.metadata.structureId);
	if (structure == null) {
		return null;
	}
	const devices = Linq.AsEnumerable<Device>(structure.devices);
	var device = devices.FirstOrDefault((device: Device) => device.deviceId === measurementData.metadata.deviceId);

	if (device == null) {
		applicationLogger.debug(`Cannot find a device with id: ${measurementData.metadata.deviceId}, attempting to find the device via its parentId.`);
		if (measurementData.metadata.parentId !== undefined) {
			const parentDevice = devices.FirstOrDefault((device: Device) => device.id === measurementData.metadata.parentId);
			if (parentDevice == null) {
				applicationLogger.debug(`Could not find a parent device with id: ${measurementData.metadata.parentId}, returning null.`);
				return null;
			}
			const children = Linq.AsEnumerable<Device>(parentDevice.children);
			device = children.FirstOrDefault((device: Device) => device.deviceId === measurementData.metadata.deviceId);
			return device;
		}
	}
	
	return device;
}

//function getWarning(location: Location, measurementData: MeasurementViewModel): MeasurementViewModel {
//	const warnings = Linq.AsEnumerable<MeasurementViewModel>(location.currentMeasurements);
//	return warnings.FirstOrDefault((warning: MeasurementViewModel) => warning.metadata.deviceId === measurementData.metadata.deviceId);
//}

export async function getZoneMeasurements(state: IState, locationId: string, zoneId: string) {
	const measurementRequest = new MeasurementRequestViewModel(null, zoneId, null, null);
	const newState = Object.assign({}, state);
	const location = Linq.AsEnumerable<Location>(newState.locationState.locations).FirstOrDefault(loc => loc.id === locationId);

	if (location !== undefined) {
		const measurements = await api.getMeasurements(measurementRequest);
		measurements.forEach(measurement => {
			const device = getDevice(location, measurement);
			if (device) {
				device.deviceStyle = measurement.deviceStyle;
				device.data = measurement.data;
				device.styles = measurement.styles;
				device.properties = measurement.metadata.properties;
				device.metadata = measurement.metadata;
			}
		});
		dispatchify(updateLocation)(location);
	}
	return newState;
}



export async function getLocationWarnings(state: IState, locationId: string) {
	const measurementRequest = new MeasurementRequestViewModel(locationId, null, null, null);
	const newState = Object.assign({}, state);
	const location = Linq.AsEnumerable<Location>(newState.locationState.locations).FirstOrDefault(loc => loc.id === locationId);
	const measurements = await api.getWarnings(measurementRequest);
	measurements.forEach(measurement => {

		const warning = Object.assign({}, measurement);
		
		if (location !== undefined && location.currentMeasurements !== undefined && location.currentMeasurements) {

			const index = location.currentMeasurements.findIndex((m => m.metadata.deviceId === warning.metadata.deviceId) as any);
			if (warning.deviceStyle !== StatusColors.green) {

				if (index > -1) {
					// move the warning to the top of the list.
					const existingWarning = location.currentMeasurements[index];
					if (existingWarning.deviceStyle !== warning.deviceStyle) {
						// if the device style changes move the warning to the top of the list
						location.currentMeasurements.splice(index, 1);
						location.currentMeasurements.unshift(warning);
					} else {
						// otherwise just update the warning
						existingWarning.deviceStyle = measurement.deviceStyle;
						existingWarning.metadata = measurement.metadata;
						existingWarning.warningMessages = measurement.warningMessages;
						existingWarning.data = measurement.data;
						existingWarning.styles = measurement.styles;
						existingWarning.properties = measurement.metadata.properties;
						existingWarning.metadata = measurement.metadata;
					}

				} else {
					// push the new warning to the top of the list
					location.currentMeasurements.unshift(warning);
				}
			} else {
				if (index > -1) {
					location.currentMeasurements.splice(index, 1);
				}
			}
		}
	});

	if (location !== undefined) {
		dispatchify(updateLocation)(location);
	}
	
	return newState;
}

function updateDevice(device: Device, measurementData: MeasurementViewModel) {
	device.deviceStyle = measurementData.deviceStyle;
	device.data = measurementData.data;
	device.styles = measurementData.styles;
	device.messages = measurementData.warningMessages;
	device.properties = measurementData.metadata.properties;
	device.metadata = measurementData.metadata;
}

function updateWarnings(location: Location, measurementData: MeasurementViewModel) {
	const warning = Object.assign({}, measurementData);
	const index = location.currentMeasurements.findIndex((m => m.metadata.deviceId === warning.metadata.deviceId) as any);
	if (warning.deviceStyle !== StatusColors.green) {
		if (index > -1) {
			// move the warning to the top of the list.
			const existingWarning = location.currentMeasurements[index];
			if (existingWarning.deviceStyle !== warning.deviceStyle) {
				location.currentMeasurements.splice(index, 1);
				location.currentMeasurements.unshift(warning);
			} else {
				existingWarning.deviceStyle = measurementData.deviceStyle;
				existingWarning.metadata = measurementData.metadata;
				existingWarning.warningMessages = measurementData.warningMessages;
				existingWarning.data = measurementData.data;
				existingWarning.styles = measurementData.styles;
				existingWarning.properties = measurementData.metadata.properties;
				existingWarning.metadata = measurementData.metadata;
			}
		} else {
			location.currentMeasurements.push(warning);
		}
	} else {
		if (index > -1) {
			location.currentMeasurements.splice(index, 1);
		}
	}
}

export async function processMeasurement(state: IState, measurementData: MeasurementViewModel): Promise<IState> {
	applicationLogger.debug(JSON.stringify(measurementData));

	const newState = Object.assign({}, state);
	const locationList = Linq.AsEnumerable<Location>(newState.locationState.selectedLocations);
	const location = locationList.FirstOrDefault(l => l.id === measurementData.metadata.locationId);

	if (!location) {
		applicationLogger.debug(`Cannot find a location with id:${measurementData.metadata.locationId} associated to device with id: ${measurementData.metadata.deviceId}`);
		return state;
	}
	const device = getDevice(location, measurementData);

	if (!device) {
		applicationLogger.debug(`Cannot find a device with id: ${measurementData.metadata.deviceId}`);
		return state;
	}

	updateDevice(device, measurementData);
	updateWarnings(location, measurementData);

	return newState;
}

// register actions here...
// store.registerAction("addWarning", addWarning);
store.registerAction("getZoneMeasurements", getZoneMeasurements);
store.registerAction("getLocationWarnings", getLocationWarnings);
store.registerAction("processMeasurement", processMeasurement);
